import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import EmptyState from "@/components/EmptyState";
import {
  TrendingUp,
  TrendingDown,
  Target,
  Activity,
  BarChart3,
  Trophy,
  ArrowUpDown,
  Layers,
  PieChart as PieChartIcon,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { API_URL } from "@/lib/api";

const API = API_URL;

const SORT_OPTIONS = [
  { value: "pnl", label: "Total P/L" },
  { value: "winRate", label: "Win Rate" },
  { value: "trades", label: "Trade Count" },
];

const CHART_COLORS = [
  "#1877F2",
  "#f97316",
  "#22c55e",
  "#ef4444",
  "#a855f7",
  "#06b6d4",
  "#eab308",
  "#ec4899",
  "#14b8a6",
  "#6366f1",
];

function formatCurrency(value, currency = "") {
  if (value === undefined || value === null) return "—";
  const formatted = Number(value).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

function SkeletonPortfolio() {
  return (
    <div className="p-4 md:p-6">
      <div className="mb-8">
        <Skeleton className="h-10 w-56 mb-2" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-8 rounded-lg" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-24 mb-1" />
              <Skeleton className="h-3 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Card className="shadow-lg">
          <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
          <CardContent style={{ height: 300 }}><Skeleton className="h-full w-full rounded" /></CardContent>
        </Card>
        <Card className="shadow-lg">
          <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
          <CardContent style={{ height: 300 }}><Skeleton className="h-full w-full rounded" /></CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function PortfolioView({ currentStrategy, mode }) {
  const [strategies, setStrategies] = useState([]);
  const [statisticsMap, setStatisticsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("pnl");

  const loadPortfolioData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const strategiesRes = await axios.get(`${API}/strategies?mode=${mode}`, { headers });
      const strategiesData = strategiesRes.data;
      setStrategies(strategiesData);

      // Load statistics for each strategy in parallel
      const statsPromises = strategiesData.map((s) =>
        axios
          .get(`${API}/statistics/${s.id}?mode=${mode}`, { headers })
          .then((res) => ({ id: s.id, data: res.data }))
          .catch(() => ({ id: s.id, data: null }))
      );

      const statsResults = await Promise.all(statsPromises);
      const map = {};
      statsResults.forEach(({ id, data }) => {
        map[id] = data;
      });
      setStatisticsMap(map);
    } catch (error) {
      console.error("Error loading portfolio:", error);
      toast.error("Failed to load portfolio data");
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    loadPortfolioData();
  }, [loadPortfolioData]);

  // Compute metrics per strategy
  const strategyMetrics = useMemo(() => {
    return strategies.map((s) => {
      const stats = statisticsMap[s.id];
      if (!stats || stats.total_trades === 0) {
        return {
          ...s,
          totalPnL: 0,
          winRate: 0,
          tradeCount: 0,
          hasData: false,
        };
      }
      return {
        ...s,
        totalPnL: stats.total_profit_loss || 0,
        winRate: stats.win_rate || 0,
        tradeCount: stats.total_trades || 0,
        winningTrades: stats.winning_trades || 0,
        losingTrades: stats.losing_trades || 0,
        profitFactor: stats.profit_factor || 0,
        maxDrawdown: stats.max_drawdown || 0,
        hasData: true,
      };
    });
  }, [strategies, statisticsMap]);

  // Sort strategies
  const sortedStrategies = useMemo(() => {
    return [...strategyMetrics].sort((a, b) => {
      switch (sortBy) {
        case "pnl":
          return b.totalPnL - a.totalPnL;
        case "winRate":
          return b.winRate - a.winRate;
        case "trades":
          return b.tradeCount - a.tradeCount;
        default:
          return 0;
      }
    });
  }, [strategyMetrics, sortBy]);

  // Overall portfolio metrics
  const portfolioMetrics = useMemo(() => {
    const withData = strategyMetrics.filter((s) => s.hasData);
    const totalPnL = withData.reduce((sum, s) => sum + s.totalPnL, 0);
    const totalTrades = withData.reduce((sum, s) => sum + s.tradeCount, 0);
    const totalWins = withData.reduce((sum, s) => sum + (s.winningTrades || 0), 0);
    const totalLosses = withData.reduce((sum, s) => sum + (s.losingTrades || 0), 0);
    const winRate = totalTrades > 0 ? ((totalWins / totalTrades) * 100).toFixed(1) : 0;

    // Diversification score: based on how many strategies have data and distribution of trades
    const activeStrategies = withData.length;
    const tradeCounts = withData.map((s) => s.tradeCount);
    const maxTrades = Math.max(...tradeCounts, 1);
    const evenness =
      tradeCounts.length > 0
        ? tradeCounts.reduce((sum, c) => sum + Math.min(c / maxTrades, 1), 0) /
          tradeCounts.length
        : 0;
    const diversificationScore = Math.round(evenness * Math.min(activeStrategies / 3, 1) * 100);

    return {
      totalPnL,
      totalTrades,
      totalWins,
      totalLosses,
      winRate,
      activeStrategies,
      diversificationScore: Math.min(diversificationScore, 100),
    };
  }, [strategyMetrics]);

  // Pie chart data: allocation by strategy (based on trade count)
  const allocationData = useMemo(() => {
    const withData = strategyMetrics.filter((s) => s.hasData && s.tradeCount > 0);
    return withData.map((s, i) => ({
      name: s.name,
      value: s.tradeCount,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
  }, [strategyMetrics]);

  // Bar chart: P/L comparison
  const pnlBarData = useMemo(() => {
    return sortedStrategies
      .filter((s) => s.hasData)
      .map((s, i) => ({
        name: s.name.length > 12 ? s.name.slice(0, 12) + "…" : s.name,
        pnl: s.totalPnL,
        fill: s.totalPnL >= 0 ? "#22c55e" : "#ef4444",
      }));
  }, [sortedStrategies]);

  if (loading) {
    return <SkeletonPortfolio />;
  }

  if (strategies.length === 0) {
    return (
      <div className="p-4 md:p-6" data-testid="no-portfolio-data">
        <EmptyState
          icon={Layers}
          title="No strategies yet"
          description="Create at least one strategy to view your portfolio overview."
          actionLabel="Go to Dashboard"
          onAction={() => (window.location.href = `/${mode}`)}
        />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 min-h-screen" data-testid="portfolio-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
              Portfolio Overview
            </h1>
            <p className="text-muted-foreground text-sm md:text-base">
              Multi-strategy performance across your portfolio
            </p>
          </div>
          <div className="flex items-center gap-2">
            {SORT_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={sortBy === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setSortBy(opt.value)}
                className="rounded-full text-xs"
              >
                <ArrowUpDown className="h-3 w-3 mr-1" />
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Overall Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <Card className="shadow-sm hover:shadow-md transition-all border-border/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Portfolio P/L
              </CardTitle>
              <div
                className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                  portfolioMetrics.totalPnL >= 0 ? "bg-success" : "bg-destructive"
                }`}
              >
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${
                  portfolioMetrics.totalPnL >= 0 ? "text-success" : "text-destructive"
                }`}
              >
                {portfolioMetrics.totalPnL >= 0 ? "+" : ""}
                {formatCurrency(portfolioMetrics.totalPnL)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Across {portfolioMetrics.activeStrategies} strategies
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-all border-border/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Portfolio Win Rate
              </CardTitle>
              <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-primary/10">
                <Target className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{portfolioMetrics.winRate}%</div>
              <p className="text-xs text-muted-foreground mt-1">
                {portfolioMetrics.totalWins}W / {portfolioMetrics.totalLosses}L
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-all border-border/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Trades
              </CardTitle>
              <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-orange-500/10">
                <Activity className="h-4 w-4 text-orange-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{portfolioMetrics.totalTrades}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {portfolioMetrics.activeStrategies} active strategies
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm hover:shadow-md transition-all border-border/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Diversification
              </CardTitle>
              <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-blue-500/10">
                <PieChartIcon className="h-4 w-4 text-blue-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {portfolioMetrics.diversificationScore}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Trade distribution score
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* Allocation Pie Chart */}
          <Card className="shadow-lg border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Strategy Allocation</CardTitle>
              <CardDescription>Trade distribution by strategy</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-center" style={{ height: 300 }}>
              {allocationData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={allocationData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name.length > 10 ? name.slice(0, 10) + "…" : name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {allocationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "0.5rem",
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted-foreground text-sm">No trade data</div>
              )}
            </CardContent>
          </Card>

          {/* P/L Comparison Bar Chart */}
          <Card className="shadow-lg border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">P/L by Strategy</CardTitle>
              <CardDescription>Compare total profit/loss across strategies</CardDescription>
            </CardHeader>
            <CardContent style={{ height: 300 }}>
              {pnlBarData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pnlBarData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      type="number"
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fontSize: 11 }}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "0.5rem",
                      }}
                    />
                    <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                      {pnlBarData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  <BarChart3 className="h-8 w-8 mr-2 opacity-50" />
                  No data to display
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Strategy Comparison Table */}
        <Card className="shadow-lg border-border/50 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Strategy Comparison</CardTitle>
                <CardDescription>Side-by-side performance breakdown</CardDescription>
              </div>
              <Badge variant="outline" className="text-xs">
                Sorted by: {SORT_OPTIONS.find((o) => o.value === sortBy)?.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Strategy
                  </th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Total P/L
                  </th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Win Rate
                  </th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Trades
                  </th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    W / L
                  </th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                    Max DD
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedStrategies.map((s, i) => (
                  <motion.tr
                    key={s.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className={`border-b border-border/50 hover:bg-secondary/30 transition-colors ${
                      i === 0 ? "bg-primary/5" : ""
                    }`}
                  >
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {i === 0 && s.hasData && (
                          <Trophy className="h-3.5 w-3.5 text-yellow-500" />
                        )}
                        <div>
                          <p className="font-semibold text-sm">{s.name}</p>
                          <p className="text-xs text-muted-foreground">{s.currency}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <span
                        className={`font-bold ${
                          s.totalPnL >= 0 ? "text-success" : "text-destructive"
                        }`}
                      >
                        {s.hasData ? (
                          <>
                            {s.totalPnL >= 0 ? "+" : ""}
                            {formatCurrency(s.totalPnL)}
                          </>
                        ) : (
                          "—"
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <div className="w-12 h-1.5 bg-secondary/50 rounded-full overflow-hidden hidden sm:block">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${s.winRate}%` }}
                          />
                        </div>
                        <span className="font-medium">{s.hasData ? `${s.winRate}%` : "—"}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-right font-medium">{s.tradeCount || "—"}</td>
                    <td className="py-3 px-3 text-right text-xs">
                      {s.hasData ? (
                        <span>
                          <span className="text-success">{s.winningTrades}</span>
                          <span className="text-muted-foreground mx-0.5">/</span>
                          <span className="text-destructive">{s.losingTrades}</span>
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-3 px-3 text-right text-xs text-destructive hidden sm:table-cell">
                      {s.hasData ? `${s.maxDrawdown}%` : "—"}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Strategy Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {sortedStrategies.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className="shadow-sm hover:shadow-md transition-all border-border/50 h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                        style={{
                          backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                        }}
                      >
                        {s.name?.charAt(0)}
                      </div>
                      <div>
                        <CardTitle className="text-sm font-bold">{s.name}</CardTitle>
                        <CardDescription className="text-xs">{s.currency}</CardDescription>
                      </div>
                    </div>
                    {i === 0 && s.hasData && (
                      <Badge className="bg-yellow-500/15 text-yellow-600 border-yellow-500/30 text-[10px]">
                        <Trophy className="h-3 w-3 mr-1" />
                        Best
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {s.hasData ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Total P/L</span>
                        <span
                          className={`font-bold ${
                            s.totalPnL >= 0 ? "text-success" : "text-destructive"
                          }`}
                        >
                          {s.totalPnL >= 0 ? "+" : ""}
                          {formatCurrency(s.totalPnL)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Win Rate</span>
                        <span className="font-semibold">{s.winRate}%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Trades</span>
                        <span className="font-semibold">{s.tradeCount}</span>
                      </div>
                      <div className="w-full h-1.5 bg-secondary/50 rounded-full overflow-hidden mt-2">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${s.winRate}%`,
                            backgroundColor:
                              s.totalPnL >= 0
                                ? "hsl(var(--success))"
                                : "hsl(var(--destructive))",
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-xs">
                      No trades recorded yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
